import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  HrCertificate,
  HrPerson,
  RaiseTimeOff,
  RotaWeek,
  TimeOffRequest,
} from "@dentacore/shared-types";

import { readMe, renewCertificate } from "../api/hr";
import { readWeek } from "../api/rota";
import { listTimeOff, raiseTimeOff, withdrawTimeOff } from "../api/time-off";
import { describe } from "../settings/use-writer";
import type { Writer } from "../settings/use-writer";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { Pager, useFitPages } from "../ui/paging";
import { addDays, datesOf, daysText, hoursOf, localToday, roleLabel, shiftText } from "./roster";
import styles from "./staff.module.css";
import { CONTRACT_LABELS, PERSONAL_LABELS, daysBetween, personalFacts, shortDate } from "./team";
import own from "./me.module.css";
import list from "./team.module.css";
import { RenewDialog } from "./team-dialogs";
import { Mark } from "./team-mark";
import { RaiseDialog } from "./time-off-view";
import { mondayOf, weekLabel } from "./weeks";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The contract's rows, in the order `contractFacts` draws them. */
const FACT_LABELS = [
  "Contract",
  "Started",
  "Probation",
  "Notice",
  "Last day",
  "Works at",
  ...PERSONAL_LABELS,
];

/** One working stretch on My week, and where it is. */
interface Shift {
  text: string;
  location: string;
}

/** One location's week, or why it is not shown. */
type LocationWeek = { name: string; week: RotaWeek | null; failed: boolean };

/**
 * Staff → Me (STORY-181e; HR-D1, HR-D4, HR-D7, HR-D11, HR-D18, HR-D19).
 *
 * **One person's own record, and nobody else's**: their week, their leave,
 * their certificates and their contract — never a colleague, never a salary.
 * It is the Staff screen for everybody without `hr.team.view`, and every
 * function their old Time off tab gave them is here: asking, and now
 * withdrawing a request that still waits.
 *
 * **My week spans every location they work at** (HR-D19), each read through
 * the same per-person week Schedule draws at that location — publication rule
 * included — so a day here is exactly what Schedule shows there.
 */
export function MeView({
  writer,
}: {
  writer: Pick<Writer, "busy" | "run" | "fail">;
}): React.JSX.Element {
  const { busy, run, fail } = writer;
  const timezone = useBranchTimezone();
  const today = localToday(timezone);

  const [me, setMe] = useState<HrPerson | null>(null);
  const [requests, setRequests] = useState<readonly TimeOffRequest[] | null>(null);
  const [monday, setMonday] = useState(() => mondayOf(today));
  const [weeks, setWeeks] = useState<readonly LocationWeek[] | null>(null);
  const [asking, setAsking] = useState(false);
  const [withdrawing, setWithdrawing] = useState<TimeOffRequest | null>(null);
  const [renewing, setRenewing] = useState<HrCertificate | null>(null);
  const [justAsked, setJustAsked] = useState<{ key: string } | null>(null);

  const reloadMe = useCallback(async (signal?: AbortSignal) => {
    setMe(await readMe(signal));
  }, []);
  const reloadRequests = useCallback(async (signal?: AbortSignal) => {
    setRequests(await listTimeOff("ALL", signal));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([reloadMe(controller.signal), reloadRequests(controller.signal)]).catch(
      (cause: unknown) => {
        if (controller.signal.aborted) return;
        fail(describe(cause, "Could not read your record."));
      },
    );
    return () => controller.abort();
  }, [reloadMe, reloadRequests, fail]);

  // One read per location (HR-D19). A location that does not answer says so
  // in its own days rather than blanking the whole week. The last week stays
  // drawn until the next arrives, so a re-read after an upload does not flash.
  useEffect(() => {
    if (me === null) return;
    const controller = new AbortController();
    Promise.all(
      me.locations.map((location) =>
        readWeek(me.userId, monday, controller.signal, location.id)
          .then((week): LocationWeek => ({ name: location.name, week, failed: false }))
          .catch((): LocationWeek => ({ name: location.name, week: null, failed: true })),
      ),
    ).then((rows) => {
      if (!controller.signal.aborted) setWeeks(rows);
    });
    return () => controller.abort();
  }, [me, monday]);

  /** Each date's shifts across every location, and which locations withheld it. */
  const days = useMemo(() => {
    const unpublished = (weeks ?? [])
      .filter((row) => row.week !== null && row.week.days.length === 0)
      .map((row) => row.name);
    const failed = (weeks ?? []).filter((row) => row.failed).map((row) => row.name);
    const named = (me?.locations.length ?? 0) > 1;
    const byDate = datesOf(monday).map((date) => {
      const shifts: Shift[] = [];
      for (const row of weeks ?? []) {
        const day = row.week?.days.find((entry) => entry.date === date);
        if (day === undefined || day.kind === "OFF") continue;
        shifts.push({ text: shiftText(day), location: named ? row.name : "" });
      }
      return { date, shifts };
    });
    return { byDate, unpublished, failed };
  }, [weeks, monday, me?.locations.length]);

  const mine = useMemo(
    () =>
      (requests ?? [])
        .filter((row) => row.userId === me?.userId)
        .filter(
          (row) => row.status === "PENDING" || (row.status === "APPROVED" && row.endsOn >= today),
        )
        .sort((a, b) => a.startsOn.localeCompare(b.startsOn)),
    [requests, me?.userId, today],
  );

  const requestFit = useFitPages({
    keys: mine.map((row) => row.id),
    estimate: () => 49,
    gap: 0,
    // A request just asked for is shown where it sorted, not left on a page unseen.
    follow: justAsked,
  });
  const certificateFit = useFitPages({
    keys: (me?.certificates ?? []).map((row) => row.id),
    estimate: () => 61,
    gap: 0,
  });
  // The contract pages too: six facts do not fit a laptop's card (PF4).
  const factFit = useFitPages({ keys: FACT_LABELS, estimate: () => 37, gap: 0 });

  if (me === null) {
    return (
      <div className={own.page} data-testid="me-view">
        <p className={styles.blank}>Reading&hellip;</p>
      </div>
    );
  }

  const notice = noticeFor(me, today);
  const [requestStart, requestEnd] = requestFit.range;
  const [certStart, certEnd] = certificateFit.range;
  const left = me.leave.remainingDays;

  return (
    <div className={own.page} data-testid="me-view">
      {notice !== null && (
        <div className={own.notice} role="status" data-testid="me-notice">
          <Mark tone={notice.tone} />
          <p>
            <b>{notice.headline}</b> {notice.detail}
          </p>
          {notice.certificate !== null && (
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => setRenewing(notice.certificate)}
              data-testid="me-notice-upload"
            >
              Upload renewal
            </button>
          )}
        </div>
      )}

      <section className={own.week} aria-label="My week" data-testid="me-week">
        <header className={own.weekHead}>
          <b>My week</b>
          <span className={list.sub}>{weekLabel(monday)}</span>
          <span className={own.weekNav}>
            <button
              type="button"
              className={styles.quiet}
              aria-label="Previous week"
              onClick={() => setMonday(addDays(monday, -7))}
              data-testid="me-week-prev"
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.quiet}
              disabled={monday === mondayOf(today)}
              onClick={() => setMonday(mondayOf(today))}
            >
              This week
            </button>
            <button
              type="button"
              className={styles.quiet}
              aria-label="Next week"
              onClick={() => setMonday(addDays(monday, 7))}
              data-testid="me-week-next"
            >
              ›
            </button>
          </span>
        </header>
        <div className={own.days}>
          {days.byDate.map(({ date, shifts }, index) => (
            <div
              key={date}
              className={date === today ? `${own.day} ${own.today}` : own.day}
              data-testid="me-day"
              data-date={date}
            >
              <span className={own.dayName}>
                {WEEKDAYS[index]} <b>{Number(date.slice(8))}</b>
                {date === today && <span className={own.todayWord}>Today</span>}
              </span>
              {weeks === null ? null : shifts.length === 0 ? (
                <span className={list.sub}>Off</span>
              ) : (
                shifts.map((shift) => (
                  <span
                    key={`${shift.location}-${shift.text}`}
                    className={own.shift}
                    data-role={me.role}
                    data-testid="me-shift"
                  >
                    <b>{shift.text}</b>
                    {shift.location !== "" && <span>{shift.location}</span>}
                  </span>
                ))
              )}
            </div>
          ))}
        </div>
        <footer className={own.weekFoot}>
          <span>
            {me.contract.contractedMinutes === null
              ? "No contracted hours set"
              : `Contracted ${hoursOf(me.contract.contractedMinutes)} a week`}
          </span>
          <span className={list.sub}>
            {days.failed.length > 0
              ? `${days.failed.join(", ")} did not answer.`
              : days.unpublished.length > 0
                ? `Not published yet at ${days.unpublished.join(", ")}.`
                : "From the rota your manager publishes"}
          </span>
        </footer>
      </section>

      <div className={own.cards}>
        <section className={own.card} aria-label="Leave" data-testid="me-leave">
          <header className={own.cardHead}>
            <b>Leave</b>
            <span className={list.sub}>{me.leave.year}</span>
          </header>
          <div className={own.leaveFigure}>
            {left === null ? (
              <span className={list.sub}>No allowance set — ask your manager.</span>
            ) : (
              <>
                <b className={left < 0 ? list.warmText : undefined}>{daysText(Math.abs(left))}</b>
                <span>
                  {left < 0
                    ? "days over the allowance"
                    : `of ${daysText(me.leave.entitlementDays ?? 0)} days left`}
                </span>
              </>
            )}
            <span className={own.leaveSplit}>
              {daysText(me.leave.takenDays)} taken · {daysText(me.leave.approvedDays)} booked ·{" "}
              {daysText(me.leave.pendingDays)} waiting
            </span>
          </div>
          <div className={own.area} ref={requestFit.areaRef} data-testid="me-requests">
            {requests !== null && mine.length === 0 ? (
              <p className={own.empty}>Nothing asked for. Time off you ask for shows here.</p>
            ) : (
              mine.slice(requestStart, requestEnd).map((row) => (
                <div
                  key={row.id}
                  ref={requestFit.measure(row.id)}
                  className={own.item}
                  data-testid="me-request"
                >
                  <span className={own.itemText}>
                    <b>
                      {row.startsOn === row.endsOn
                        ? shortDate(row.startsOn)
                        : `${shortDate(row.startsOn)} – ${shortDate(row.endsOn)}`}
                    </b>
                    <span className={list.sub}>
                      {roleLabel(row.type)} · {daysText(row.days)} {row.days === 1 ? "day" : "days"}{" "}
                      · {row.status === "PENDING" ? "waiting for a decision" : "approved"}
                      {row.reason !== null && row.reason !== "" && ` · ${row.reason}`}
                    </span>
                  </span>
                  {row.status === "PENDING" && (
                    <button
                      type="button"
                      className={styles.quiet}
                      disabled={busy}
                      onClick={() => setWithdrawing(row)}
                      data-testid="me-withdraw"
                    >
                      Withdraw
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          <footer className={own.cardFoot}>
            <Pager
              page={requestFit.page}
              count={requestFit.pages.length}
              onPage={requestFit.setPage}
              from={requestStart + 1}
              to={requestEnd}
              total={mine.length}
              noun="requests"
              compact
              testId="me-requests-pager"
            />
            <span className={list.footNote}>Your manager decides</span>
            <button
              type="button"
              className={styles.primary}
              disabled={busy}
              onClick={() => setAsking(true)}
              data-testid="me-ask"
            >
              Ask for time off
            </button>
          </footer>
        </section>

        <section className={own.card} aria-label="Certificates" data-testid="me-certificates">
          <header className={own.cardHead}>
            <b>Certificates</b>
            <span className={list.count}>{me.certificates.length}</span>
          </header>
          <div className={own.area} ref={certificateFit.areaRef}>
            {me.certificates.length === 0 ? (
              <p className={own.empty}>None recorded. Your manager adds them.</p>
            ) : (
              me.certificates.slice(certStart, certEnd).map((row) => (
                <div
                  key={row.id}
                  ref={certificateFit.measure(row.id)}
                  className={own.item}
                  data-testid="me-certificate"
                >
                  <Mark
                    tone={row.state === "EXPIRED" ? "bad" : row.state === "DUE" ? "warm" : "calm"}
                  />
                  <span className={own.itemText}>
                    <b>{row.name}</b>
                    <span className={own.line}>{certificateLine(row, today)}</span>
                    {row.pendingRenewal !== null && (
                      <span className={own.line} data-testid="me-renewal-waiting">
                        Renewal uploaded — waiting for a check
                      </span>
                    )}
                    {row.pendingRenewal === null && row.rejection !== null && (
                      <span className={list.warmText} data-testid="me-renewal-rejected">
                        Your renewal was rejected
                        {row.rejection.reason === null ? "." : `: ${row.rejection.reason}`}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className={row.state === "EXPIRED" ? styles.primary : styles.ghost}
                    disabled={busy}
                    onClick={() => setRenewing(row)}
                    data-testid="me-upload"
                  >
                    {row.pendingRenewal === null ? "Upload renewal" : "Upload again"}
                  </button>
                </div>
              ))
            )}
          </div>
          <footer className={own.cardFoot}>
            <Pager
              page={certificateFit.page}
              count={certificateFit.pages.length}
              onPage={certificateFit.setPage}
              from={certStart + 1}
              to={certEnd}
              total={me.certificates.length}
              noun="certificates"
              compact
              testId="me-certificates-pager"
            />
            <span className={list.footNote}>A manager checks what you upload</span>
          </footer>
        </section>

        <section className={own.card} aria-label="My contract" data-testid="me-contract">
          <header className={own.cardHead}>
            <b>My contract</b>
          </header>
          <div className={own.area} ref={factFit.areaRef}>
            <dl className={list.facts} data-testid="me-contract-facts">
              {contractFacts(me, today)
                .slice(factFit.range[0], factFit.range[1])
                .map(([label, value]) => (
                  <div key={label} ref={factFit.measure(label)} className={list.fact}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
            </dl>
          </div>
          <footer className={own.cardFoot}>
            <Pager
              page={factFit.page}
              count={factFit.pages.length}
              onPage={factFit.setPage}
              from={factFit.range[0] + 1}
              to={factFit.range[1]}
              total={FACT_LABELS.length}
              noun="facts"
              compact
              testId="me-contract-pager"
            />
            <span className={list.footNote}>Set by the practice</span>
          </footer>
        </section>
      </div>

      {asking && (
        <RaiseDialog
          people={[]}
          meId={me.userId}
          busy={busy}
          onRaise={(body: RaiseTimeOff) => {
            void run(
              () => raiseTimeOff(body),
              async () => {
                await Promise.all([reloadRequests(), reloadMe()]);
              },
              (raised) => {
                setJustAsked({ key: (raised as TimeOffRequest).id });
                return "Asked. It is waiting on a decision.";
              },
            ).then((ok) => {
              if (ok) setAsking(false);
            });
          }}
          onClose={() => setAsking(false)}
        />
      )}

      {withdrawing !== null && (
        <ConfirmDialog
          title="Withdraw this request?"
          confirmLabel="Withdraw it"
          busy={busy}
          onCancel={() => setWithdrawing(null)}
          onConfirm={() => {
            const row = withdrawing;
            void run(
              () => withdrawTimeOff(row.id),
              async () => {
                await Promise.all([reloadRequests(), reloadMe()]);
              },
              "Withdrawn.",
            ).then((ok) => {
              if (ok) setWithdrawing(null);
            });
          }}
          testId="me-withdraw-confirm"
        >
          <p>
            Your request for{" "}
            {withdrawing.startsOn === withdrawing.endsOn
              ? shortDate(withdrawing.startsOn)
              : `${shortDate(withdrawing.startsOn)} – ${shortDate(withdrawing.endsOn)}`}{" "}
            goes, before anybody decides it. Ask again any time.
          </p>
        </ConfirmDialog>
      )}

      {renewing !== null && (
        <RenewDialog
          name={me.name}
          certificate={renewing}
          countsAtOnce={false}
          busy={busy}
          onSave={(expiresOn, file) => {
            void run(
              () => renewCertificate(renewing.id, expiresOn, file),
              () => reloadMe(),
              "Uploaded. A manager will check it.",
            ).then((ok) => {
              if (ok) setRenewing(null);
            });
          }}
          onClose={() => setRenewing(null)}
        />
      )}
    </div>
  );
}

/**
 * The one thing on their record most worth saying first (HR-D3, HR-D7): a
 * lapsed certificate needed to treat, then any lapsed one, then one due.
 */
function noticeFor(
  me: HrPerson,
  today: string,
): {
  tone: "bad" | "warm";
  headline: string;
  detail: string;
  certificate: HrCertificate | null;
} | null {
  const expired = me.certificates.filter((row) => row.state === "EXPIRED");
  const lapsed = expired.find((row) => row.neededToTreat) ?? expired[0];
  if (lapsed !== undefined && lapsed.expiresOn !== null) {
    const ago = daysBetween(lapsed.expiresOn, today);
    return {
      tone: "bad",
      headline: `Your ${lapsed.name} ran out ${String(ago)} ${ago === 1 ? "day" : "days"} ago.`,
      detail: lapsed.neededToTreat
        ? "You can still be booked, but you should not be treating until it is renewed."
        : "Upload the renewal when you have it.",
      certificate: lapsed.pendingRenewal === null ? lapsed : null,
    };
  }
  const due = me.certificates.find((row) => row.state === "DUE");
  if (due !== undefined && due.expiresOn !== null) {
    const ahead = daysBetween(today, due.expiresOn);
    return {
      tone: "warm",
      headline: `Your ${due.name} runs out in ${String(ahead)} ${ahead === 1 ? "day" : "days"}.`,
      detail: "Upload the renewal when you have it.",
      certificate: due.pendingRenewal === null ? due : null,
    };
  }
  return null;
}

function certificateLine(row: HrCertificate, today: string): string {
  if (row.expiresOn === null) return "Does not run out";
  const ahead = daysBetween(today, row.expiresOn);
  const need = row.neededToTreat ? " · needed to treat" : "";
  return ahead < 0
    ? `Expired ${String(-ahead)} ${ahead === -1 ? "day" : "days"} ago${need}`
    : `Runs out ${shortDate(row.expiresOn)}${need}`;
}

function contractFacts(me: HrPerson, today: string): [string, string][] {
  const { contract } = me;
  const type = contract.contractType === null ? "Not set" : CONTRACT_LABELS[contract.contractType];
  return [
    [
      "Contract",
      contract.contractedMinutes === null
        ? type
        : `${type} · ${hoursOf(contract.contractedMinutes)} a week`,
    ],
    ["Started", contract.startedOn === null ? "—" : shortDate(contract.startedOn)],
    [
      "Probation",
      contract.probationEndsOn === null
        ? "—"
        : contract.probationEndsOn < today
          ? `Passed · ${shortDate(contract.probationEndsOn)}`
          : `Ends ${shortDate(contract.probationEndsOn)}`,
    ],
    [
      "Notice",
      contract.noticeWeeks === null
        ? "—"
        : `${String(contract.noticeWeeks)} ${contract.noticeWeeks === 1 ? "week" : "weeks"}`,
    ],
    ["Last day", contract.endedOn === null ? "—" : shortDate(contract.endedOn)],
    ["Works at", me.locations.map((row) => row.name).join(", ") || "—"],
    // Their own personal details, blood type included (SP-D3), paged with the rest.
    ...personalFacts(me, today),
  ];
}
